import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  output,
  signal,
  viewChild,
} from '@angular/core';
import {
  FormsModule,
  NgForm,
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { ClarityModule, ClrDatagrid, ClrForm, ClrLoadingState, ClrModal } from '@clr/angular';
import { ToastService } from 'src/app/services/toast.service';
import { Subject, firstValueFrom } from 'rxjs';
import { InTableValidatorDirective } from 'src/app/directives/validators/in-table-validator.directive';
import { ArrayMethodsCallbackPipe } from 'src/app/pipes/array-methods-callback.pipe';
import { BackendLocaleDatePipe } from 'src/app/pipes/get-locale-date.pipe';
import { HideIDColumnsPipe } from 'src/app/pipes/hide-idcolumns.pipe';
import { LinieDrehungPipe } from 'src/app/pipes/linie-drehung.pipe';
import { MrTranslatePipe } from 'src/app/pipes/mr-translate.pipe';
import { ObjTypePipe } from 'src/app/pipes/objtype.pipe';
import { APIService } from 'src/app/services/APIService/api.service';
import { LinieDrehungService } from 'src/app/services/Shared/linie-drehung.service';
import { LinieValidationManager } from 'src/app/shared/classes/linie-validation-manager';
import { TablePrettyPrintPipe } from 'src/app/pipes/tablePrettyPrint.pipe';
import { OverlayService } from 'src/app/services/Overlay/overlay.service';
import { Table } from 'src/app/models/ui/table';
import { EntsperrAnlageService } from 'src/app/services/Shared/entsperr-anlage.service';
import { ConfirmService } from 'src/app/services/confirm.service';
import {
  assertBaseDataTable,
  BaseDataRoute,
  BaseItemsWrapperComponent,
} from '../../_modals/baseitems-wrapper/baseitems-wrapper.component';
import { DatalistInputComponent } from '../../_shared/datalist-input/datalist-input.component';
import { getSpezValue, round } from 'src/app/misc/functions';
import { calcPosition, GeoAutofill } from 'src/app/services/Shared/geo-autofill';

/** same as Linie + value*/
export interface SpezPointType extends SpezOptType {
  bezeichnung: any;
}

export interface SpezColumnType {
  id: string; // name
  bezeichnung: string; // orgName
  pflichtFeld?: boolean | 0 | 1;
  wListe?: string[] | null;
  optionalData?: {
    fieldType: string;
    tabId: number;
    sField: string;
  };
}

export interface SpezRowType {
  [key: string]: {
    objekt: string;
    value: any;
    orgName: string;
  };
}

export interface SpezOptType {
  objekt: string;
  orgName?: string;
  pflichtFeld?: boolean | 0 | 1;
  wListe?: string[];
  sField?: string;
  tabId?: number;
  fieldType?: string;
  isHeader?: boolean;
}

const STANDART_SPEZ_LINE = ['OSPEZID', 'Startmeter', 'Endmeter'] as const;
const STANDART_SPEZ_GLEIS = [
  ...STANDART_SPEZ_LINE,
  'Code',
  'Schienenform',
  'Schwellenart',
  'Schienenbefestigung',
  'Eindeckung',
] as const;

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'app-spezifikationsdialog',
  templateUrl: './spezifikationsdialog.component.html',
  styleUrls: ['./spezifikationsdialog.component.scss'],
  imports: [
    CommonModule,
    ClarityModule,
    FormsModule,
    ReactiveFormsModule,
    BaseItemsWrapperComponent,
    DatalistInputComponent,
    InTableValidatorDirective,
    MrTranslatePipe,
    ObjTypePipe,
    ArrayMethodsCallbackPipe,
    LinieDrehungPipe,
    HideIDColumnsPipe,
    TablePrettyPrintPipe,
    BackendLocaleDatePipe,
  ],
  providers: [LinieDrehungPipe, GeoAutofill],
})
export class SpezifikationsDialogComponent extends LinieValidationManager implements OnInit {
  private readonly apiService = inject(APIService);
  private readonly toastr = inject(ToastService);
  private readonly mrTranslate = inject(MrTranslatePipe);
  private readonly overlayService = inject(OverlayService);
  private readonly confirmService = inject(ConfirmService);
  private readonly geoAutofill = inject(GeoAutofill);
  protected readonly drehung = inject(LinieDrehungService);
  protected readonly drehungPipe = inject(LinieDrehungPipe);
  protected readonly localeDate = inject(BackendLocaleDatePipe);

  public readonly closedialog = new Subject<boolean>();
  private dataRefresh: EventEmitter<any>;
  private returnEmitter: EventEmitter<any>;
  readonly changed = output();

  private readonly clrForm = viewChild(ClrForm);
  protected readonly ngForm = viewChild(NgForm);
  private readonly templateTableGrid = viewChild('templateTableGrid', { read: ClrDatagrid });
  private readonly scrollContainer = viewChild('templateTableGrid', { read: ElementRef });
  private readonly spezTableGrid = viewChild('spezTableGrid', { read: ClrDatagrid });
  private readonly notCompleteModal = viewChild<ClrModal>('notCompleteModal');

  protected readonly isBaseItemModalOpen = signal(false);
  protected readonly editTabelle = signal<BaseDataRoute>(null);
  protected readonly saveState = signal(ClrLoadingState.DEFAULT);
  protected readonly geoState = signal(ClrLoadingState.DEFAULT);

  protected typ_Line_Point: number = 1;
  @Input() set settypeid(typeid: number) {
    this.typ_Line_Point = typeid;
  }

  private OTYPID: number;
  @Input() set setotypid(otypid: number) {
    this.OTYPID = otypid;
  }

  @Input() set setstammData(stammData: any) {
    this.startmeter = stammData.startmeter;
    this.endmeter = stammData.endmeter;
  }

  private ostammid: number;
  @Input() set setostammid(ostammid: number) {
    this.isInspektion = true;
    this.ostammid = ostammid;
  }

  public set data(dataIn: any) {
    this.ostammid = dataIn.ostamm;
    this.typ_Line_Point = dataIn.type;
    this.OTYPID = dataIn.otypid;
    this.source = dataIn.source ?? 'Inspektion';
    if (this.source == 'Projekt') {
      this.table = dataIn.tableSpez;
      this.spezData = dataIn.spezData;
    }

    this.startmeter = dataIn.stammData.startmeter;
    this.endmeter = dataIn.stammData.endmeter;

    this.dataRefresh = dataIn.dataRefresh;
    this.returnEmitter = dataIn.returnEmitter;
  }

  protected titel: string = 'Spezifikation verwalten';
  protected isInspektion: boolean = false;

  //* nur für Punktobjekte
  private OSPEZID: number;
  protected spezData: SpezPointType[];

  //* nur für Linienobjekte
  protected table: { columns: SpezColumnType[]; rows: SpezRowType[] };
  protected spezOpt: SpezOptType[] = [];
  protected setInsert: boolean = false;
  protected isSmartInsert: boolean = true;
  protected isComplete: boolean = true;
  protected direction: 1 | -1 = 1;
  protected source: 'Inspektion' | 'Projekt' = 'Inspektion'; // Quelle aus der aufgerufen wird. "Projekt" speichert/löscht nie was, das Ergebnis wird zurückgegeben
  protected exceptStartEnd = (col: SpezColumnType) => !['Startmeter', 'Endmeter'].includes(col.id);
  protected trackByOSPEZID = (row: SpezRowType) => +row?.OSPEZID.value;

  protected startmeter: number = 0;
  protected endmeter: number = 0;

  protected formSpezTop = new UntypedFormGroup({
    Startmeter: new UntypedFormControl(''),
    Endmeter: new UntypedFormControl(''),
    Code: new UntypedFormControl('', [Validators.required]),
  });

  private _selectedSpez: SpezRowType;
  protected get selectedSpez(): SpezRowType {
    return this._selectedSpez;
  }
  protected set selectedSpez(row: SpezRowType) {
    this._selectedSpez = row;
    this.formSpezTop.reset();
    if (!row) return;
    this.titel = 'Spezifikation verwalten';
    this.setInsert = false;
    this.formSpezTop.enable();
    Object.entries(this.formSpezTop.controls).forEach(([key, control]) => {
      control.setValue(row[key]?.value);
    });
    this.clrForm().markAsTouched();
    if (this.typ_Line_Point == 4 && row.Code?.value) {
      this.templateTableGrid().selected = [this.tableTmpl?.rows?.find(tmpl => tmpl.Bezeichnung == row.Code.value)];
      setTimeout(() => {
        this.scrollContainer()
          .nativeElement.querySelector('#t-' + row.Code.value)
          ?.scrollIntoView({
            behavior: 'auto',
            block: 'center',
          });
      });
    }
  }

  //* nur für Gleisobjekte
  protected tableTmpl: Table;
  protected set selectedTmpl(value: any) {
    this._selectedTmpl = value;
    this.formSpezTop?.controls.Code?.setValue(value?.Bezeichnung);
  }
  protected get selectedTmpl(): any {
    return this._selectedTmpl;
  }
  private _selectedTmpl: any;

  constructor() {
    super();
    this.form = this.formSpezTop;

    inject(EntsperrAnlageService).registerDestroyRef(inject(DestroyRef));
  }

  async ngOnInit() {
    if (!isNaN(this.startmeter) && !isNaN(this.endmeter) && !!(this.startmeter - this.endmeter)) {
      this.direction = this.initLinieValidators(this.startmeter, this.endmeter);
    }

    if (this.typ_Line_Point == 4) this.tableTmpl = await firstValueFrom(this.apiService.getSpezTemplates());

    this.initializeTables();

    this.geoAutofill.registerOSTAMMID(this.ostammid);
  }

  private async initializeTables() {
    const data = await firstValueFrom(this.apiService.getSpez(this.ostammid, this.OTYPID, this.typ_Line_Point));
    let skipArr: readonly string[];

    switch (this.typ_Line_Point) {
      case 1:
      case 3:
        this.spezData ||= data as SpezPointType[];
        this.OSPEZID = getSpezValue(this.spezData, 'OSPEZID');
        break;
      case 2:
        skipArr = STANDART_SPEZ_LINE;
        this.formSpezTop.removeControl('Code');
      case 4:
        skipArr ??= STANDART_SPEZ_GLEIS;
        this.table ??= data as { columns: SpezColumnType[]; rows: SpezRowType[] };
        this.table.columns.forEach(col => {
          if (skipArr.includes(col.id)) return;

          this.formSpezTop.addControl(col.id, new UntypedFormControl());
          if (col.pflichtFeld) this.formSpezTop.controls[col.id].addValidators(Validators.required);

          this.spezOpt.push({
            objekt: col.id,
            orgName: col.bezeichnung,
            pflichtFeld: col.pflichtFeld,
            wListe: col.wListe,
            sField: col.optionalData?.sField,
            tabId: col.optionalData?.tabId,
            fieldType: col.optionalData?.fieldType,
          });
        });
        if (!this.setInsert) this.formSpezTop.disable();
        this.checkComplete();
        break;
    }
  }

  protected openTabelle(spez: SpezPointType | SpezOptType) {
    assertBaseDataTable(spez.sField);

    this.isBaseItemModalOpen.set(true);
    this.editTabelle.set({
      tabelle: spez.sField,
      sub_id: spez.tabId,
      selected: 'bezeichnung' in spez ? spez.bezeichnung : null,
      title: spez.objekt,
    });
  }

  private getSpezList() {
    switch (this.typ_Line_Point) {
      case 1:
      case 3:
        return this.spezData;
      case 2:
      case 4:
        return this.spezOpt;
    }
  }

  private getForm() {
    switch (this.typ_Line_Point) {
      case 1:
      case 3:
        return this.ngForm();
      case 2:
      case 4:
        return this.formSpezTop;
    }
  }

  protected refreshTabelle(list: string[]) {
    const { tabelle, sub_id } = this.editTabelle();
    const listOfSpez: string[] = [];

    for (const spez of this.getSpezList()) {
      if (spez.sField == tabelle && spez.tabId == sub_id) {
        spez.wListe = list;
        listOfSpez.push(spez.objekt);
      }
    }

    setTimeout(() => {
      // ! in Timeout, weil wListe Zeit braucht, um zu updaten
      const form = this.getForm();
      for (const path of listOfSpez) {
        const ctrl = form.controls[path];
        ctrl.setValue(ctrl.value);
        form.controls[path].updateValueAndValidity();
      }
    });
  }

  private async saveSpezPoint(): Promise<boolean> {
    if (this.ngForm().pristine) {
      this.toastr.success(
        this.mrTranslate.transform('Nichts wurde verändert'),
        this.mrTranslate.transform('Spezifikationen')
      );
      return true;
    }
    const sendObj = {
      table: this.spezData,
      OSPEZID: this.OSPEZID,
      otypid: this.OTYPID,
      typ: this.typ_Line_Point,
    };

    if (this.source == 'Inspektion') {
      const { success, error, OSPEZID } = await firstValueFrom(this.apiService.setSpez(this.ostammid, sendObj));
      if (success) {
        this.toastr.success(
          this.mrTranslate.transform('Erfolgreich gespeichert'),
          this.mrTranslate.transform('Spezifikationen')
        );
        this.OSPEZID = OSPEZID;

        this.changed.emit();
        this.dataRefresh?.emit(true);
        this.ngForm().form.markAsPristine();
        return true;
      } else {
        console.log(error);
        this.toastr.error(
          this.mrTranslate.transform('Etwas ist schief gelaufen'),
          this.mrTranslate.transform('Spezifikationen')
        );
        return false;
      }
    } else if (this.source == 'Projekt') {
      this.returnEmitter.next({ table: this.spezData, typ: this.typ_Line_Point });
      this.closedialog.next(true);
      return true;
    }
  }

  private async saveSpezLine(): Promise<boolean> {
    this.saveState.set(ClrLoadingState.LOADING);

    const formValue = this.formSpezTop.getRawValue();
    const spezData = this.table.columns.map(
      (col): SpezPointType => ({
        objekt: col.id,
        bezeichnung: formValue[col.id] ?? this.selectedTmpl?.[col.id],
        orgName: col.bezeichnung,
        fieldType: col.optionalData?.fieldType,
      })
    );

    const sendObj = {
      table: spezData,
      OSPEZID: this.selectedSpez?.OSPEZID.value,
      otypid: this.OTYPID,
      typ: this.typ_Line_Point,
      smartInsert: this.setInsert && this.isSmartInsert,
      upwards: this.direction == 1,
      von: formValue['Startmeter'],
      bis: formValue['Endmeter'],
    };

    if (this.source == 'Inspektion') {
      const { success, OSPEZID, table, error } = await firstValueFrom(this.apiService.setSpez(this.ostammid, sendObj));

      if (success) {
        this.toastr.success(
          this.mrTranslate.transform('Erfolgreich gespeichert'),
          this.mrTranslate.transform('Spezifikationen')
        );

        spezData.find(spez => spez.objekt == 'OSPEZID').bezeichnung = OSPEZID;
        this.saveState.set(ClrLoadingState.SUCCESS);

        if (this.setInsert) {
          if (this.isSmartInsert && table) {
            this.table.rows = table.rows;
            this.spezTableGrid().selected = [{ OSPEZID: { value: OSPEZID } }];

            return;
          }

          this.addToTable(spezData);
        } else if (this.selectedSpez) {
          for (const spez of spezData) {
            this.selectedSpez[spez.objekt].value = spez.bezeichnung;
          }
        }
        this.checkComplete();
        this.changed.emit();
        this.dataRefresh?.emit(true);
        return true;
      } else {
        console.log(error);
        this.toastr.error(
          this.mrTranslate.transform('Etwas ist schief gelaufen'),
          this.mrTranslate.transform('Spezifikationen')
        );
        this.saveState.set(ClrLoadingState.DEFAULT);
        return false;
      }
    } else if (this.source == 'Projekt') {
      if (this.setInsert) {
        this.addToTable(spezData);
      } else if (this.selectedSpez) {
        spezData.forEach(spez => (this.selectedSpez[spez.objekt].value = spez.bezeichnung));
      }
    }
  }

  public addToTable(spezData: SpezPointType[]) {
    let neuRow: SpezRowType = {};
    spezData.forEach(spez => {
      neuRow[spez.objekt] = {
        objekt: spez.objekt,
        value: spez.bezeichnung,
        orgName: spez.orgName,
      };
    });
    this.table.rows.push(neuRow);
    this.table.rows.sort((a, b) => a.Startmeter.value - b.Startmeter.value);
    this.spezTableGrid().selected = [neuRow];
  }

  public saveIfValid(): Promise<boolean> {
    this.clrForm().markAsTouched();
    switch (this.typ_Line_Point) {
      case 1:
      case 3:
        return this.ngForm().valid && this.saveSpezPoint();
      case 2:
      case 4:
        return this.formSpezTop.valid && this.saveSpezLine();
    }
  }

  public isVollständig(startMeter: number, endMeter: number) {
    this.startmeter = startMeter;
    this.endmeter = endMeter;
    this.direction = this.initLinieValidators(this.startmeter, this.endmeter);
    this.checkComplete();
    if (!this.isComplete)
      this.toastr.error(
        this.mrTranslate.transform('Berücksichtigen Sie die Vollständigkeit'),
        this.mrTranslate.transform('Spezifikationen')
      );
    return this.isComplete;
  }

  protected addSpez() {
    this.setInsert = true;
    this.isSmartInsert = true;
    this.spezTableGrid()?.selection.clearSelection();
    this.templateTableGrid()?.selection.clearSelection();
    this.titel = 'Neue Spezifikation anlegen';
    this.formSpezTop.enable();
  }

  protected async deleteSpezLine() {
    const text = this.mrTranslate.transform('Spezifikation wirklich löschen?');
    const ok = await this.confirmService.danger(text);
    if (ok && this.selectedSpez) {
      const { success, error } =
        this.source == 'Projekt' ?
          { success: true }
        : await firstValueFrom(this.apiService.deleteSpezAndHersteller(this.selectedSpez.OSPEZID.value));

      if (success) {
        this.toastr.success(this.mrTranslate.transform('Daten gelöscht'));
        this.table.rows.splice(this.table.rows.indexOf(this.selectedSpez), 1);
        this.checkComplete();
        this.formSpezTop.disable();
        this.spezTableGrid()?.selection.clearSelection();
        this.templateTableGrid()?.selection.clearSelection();
        this.changed.emit();
        this.dataRefresh?.emit(true);
      } else {
        this.toastr.error(error, this.mrTranslate.transform('Fehler beim Löschen'));
      }
    }
  }

  private checkComplete() {
    const { laenge, min, max } = this.table.rows.reduce(
      (vals, row) => {
        vals.laenge += row.Endmeter.value - row.Startmeter.value;
        vals.min = Math.min(vals.min, row.Endmeter.value, row.Startmeter.value);
        vals.max = Math.max(vals.max, row.Endmeter.value, row.Startmeter.value);
        return vals;
      },
      { laenge: 0, min: Number.MAX_VALUE, max: Number.MIN_VALUE }
    );
    const sollLaenge = round(this.endmeter - this.startmeter);
    const sollMin = round(Math.min(this.startmeter, this.endmeter));
    const sollMax = round(Math.max(this.startmeter, this.endmeter));
    this.isComplete = round(laenge) == sollLaenge && round(min) == sollMin && round(max) == sollMax;
  }

  protected openCodesVerwaltung() {
    this.overlayService.setOverlay({ overlay: 'gleisCodeVerwaltung', isTopOverlay: true });
  }

  protected async findPositionAndAutofill(mode: 'Startmeter' | 'Endmeter') {
    this.geoState.set(ClrLoadingState.LOADING);
    const snapped = await this.geoAutofill.getSnappedLength();
    this.geoState.set(ClrLoadingState.DEFAULT);
    if (snapped === null) return;

    const position = calcPosition({
      snapped,
      start: this.startmeter,
      end: this.endmeter,
      direction: this.direction,
    });

    this.formSpezTop.controls[mode].setValue(position);
  }

  protected close() {
    if (this.source == 'Inspektion') {
      if (!this.isComplete) this.notCompleteModal().open();
      else this.closedialog.next(true);
    } else if (this.source == 'Projekt') {
      if (this.typ_Line_Point == 2 || this.typ_Line_Point == 4)
        this.returnEmitter.next({ table: this.table.rows, typ: this.typ_Line_Point });
      this.closedialog.next(true);
    }
  }
}
